import React, { useState, useEffect, useRef } from "react";
import {
  Switch,
  Stack,
  FormGroup,
  styled,
  Snackbar,
  Alert,
  Button,
  Box,
  Modal,
  CircularProgress,
} from "@mui/material";
import { jwtDecode } from "jwt-decode";
import { useDispatch, useSelector } from "react-redux";
import {
  alertSettingsActions,
  updateAlertSettingAction,
  resetAlertSettingsUpdate,
} from "../../../Actions/AlertSettingsAction";
import "../../../assets/styles/checkboxRadio.scss";
import errorSign from "../../../assets/images/errorSign.svg";
import AntSwitch from "../../AntSwitch/AntSwitch";

export const modalBoxStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 340,
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 1,
  paddingLeft: 4,
  paddingRight: 4,
  display: "flex",
  justifyContent: "center",
  flexDirection: "column",
  alignItems: "center",
};
const AlertSettings = () => {
  const [settings, setSettings] = useState({
    lockdown_notification: "0",
    fault_detection: "0",
  });
  const [userRole, setUserRole] = useState("");
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [pendingChange, setPendingChange] = useState({ key: "", value: "" });
  const [announceText, setAnnounceText] = useState("");

  const cancelBtnRef = useRef(null);

  const dispatch = useDispatch();
  const { isLoading, data, isUpdated, isUpdatedError } = useSelector(
    (state) => state.alertSettings
  );

  useEffect(() => {
    const token = JSON.parse(localStorage.getItem("token"));
    if (token) {
      const decodedToken = jwtDecode(token);
      setUserRole(decodedToken.userRole);
    }
    dispatch(alertSettingsActions());
  }, [dispatch]);

  useEffect(() => {
    if (data) {
      const settingsData = {
        lockdown_notification: "0",
        fault_detection: "0",
      };
      data.forEach((item) => {
        if (settingsData.hasOwnProperty(item.code)) {
          settingsData[item.code] = item.value;
        }
      });
      setSettings(settingsData);
    }
  }, [data]);

  useEffect(() => {
    if (isConfirmDialogOpen) {
      setTimeout(() => {
        cancelBtnRef.current.focus();
      }, 500);
    }
  }, [isConfirmDialogOpen]);

  const canEditSettings = userRole === "SUPER_ADMIN";

  const handleCloseSnackbar = () => {
    dispatch(resetAlertSettingsUpdate());
  };

  const handleToggle = (setting) => {
    if (!canEditSettings) return;
    const newValue = settings[setting] === "1" ? "0" : "1";
    setPendingChange({ key: setting, value: newValue });
    setIsConfirmDialogOpen(true);
  };

  const handleConfirmDialogClose = () => {
    setIsConfirmDialogOpen(false);
    setPendingChange({ key: "", value: "" });
  };

  const handleConfirmUpdate = () => {
    const { key, value } = pendingChange;
    setSettings((prev) => ({
      ...prev,
      [key]: value,
    }));
    dispatch(updateAlertSettingAction([{ code: key, value }]));
    setIsConfirmDialogOpen(false);
    setPendingChange({ key: "", value: "" });
  };

  const getConfirmationText = () => {
    const { key, value } = pendingChange;
    const action = value === "1" ? "enable" : "disable";
    if (key === "lockdown_notification") {
      return `Are you sure you want to ${action} Lockdown Initiation Notification?`;
    } else if (key === "fault_detection") {
      return `Are you sure you want to ${action} Fault Detection Notification?`;
    }
    return `Are you sure you want to ${action} this setting?`;
  };

  useEffect(() => {
  let timer;
  let clearTimer;

  if (isLoading) {
    setAnnounceText("Loading settings...");
  } else {
    timer = setTimeout(() => {
      setAnnounceText("Settings loaded successfully.");
      clearTimer = setTimeout(() => setAnnounceText(""), 3000);
    }, 1000);
  }

  // ✅ always return a cleanup function
  return () => {
    if (timer) clearTimeout(timer);
    if (clearTimer) clearTimeout(clearTimer);
  };
}, [isLoading]);

  return (
    <>
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {announceText}
      </div>

      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <div className="notifications_wrapper" role="region">
          <div className="gl_toggle">
            <div className="sc_heading">
              <p id="label-Lockdown_Initiation_Notification">
                {" "}
                Lockdown Initiation Notification
              </p>
              <span>
                If enabled, the user will get real-time lockdown updates of
                buildings.
              </span>
            </div>
            <div
              className="sc_toggle"
              tabIndex="0"
              role="switch"
              aria-checked={settings.lockdown_notification === "1"}
              aria-labelledby="label-Lockdown_Initiation_Notification"
              onClick={() => handleToggle("lockdown_notification")}
              onKeyDown={(e) => {
                // accessibility: toggle with Enter or Space
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleToggle("lockdown_notification");
                }
              }}
            >
              <FormGroup>
                <Stack direction="row" spacing={1} alignItems="center">
                  <AntSwitch
                    checked={settings.lockdown_notification === "1"}
                    // onChange={() => handleToggle("lockdown_notification")}
                    inputProps={{
                      "aria-hidden": true,
                      tabIndex: -1,
                    }}
                    disabled={!canEditSettings}
                  />
                </Stack>
              </FormGroup>
            </div>
          </div>

          <div className="gl_toggle">
            <div className="sc_heading">
              <p id="label-Fault_Detection_Notification">
                Fault Detection Notification
              </p>
              <span>
                If enabled, the user will get real-time updates of faulty
                devices in the buildings.
              </span>
            </div>
            <div
              className="sc_toggle"
              aria-disabled={!canEditSettings ? true : false }
              tabIndex={!canEditSettings ? -1 : 0}
              role="switch"
              aria-checked={settings.mfa_enabled === "1"}
              aria-labelledby="label-Fault_Detection_Notification"
              onClick={() => handleToggle("fault_detection")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleToggle("fault_detection");
                }
              }}
            >
              <FormGroup>
                <Stack direction="row" spacing={1} alignItems="center">
                  <AntSwitch
                    checked={settings.fault_detection === "1"}
                    inputProps={{
                      "aria-hidden": true,
                      tabIndex: -1,
                    }}
                    disabled={!canEditSettings}
                  />
                </Stack>
              </FormGroup>
            </div>
          </div>

          {(isUpdated || isUpdatedError) && (
            <Snackbar
              open
              autoHideDuration={6000}
              onClose={handleCloseSnackbar}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <Alert
                onClose={handleCloseSnackbar}
                severity={isUpdated ? "success" : "error"}
                sx={{ width: "100%" }}
              >
                {isUpdated
                  ? "Setting Saved Successfully"
                  : "Error updating setting"}
              </Alert>
            </Snackbar>
          )}
        </div>
      )}

      <div className="delte_modal_wrapper">
        <Modal open={isConfirmDialogOpen} className="delte_modal">
          <div role="none">
            <Box
              sx={modalBoxStyle}
              role="dialog"
              aria-modal="true"
              aria-labelledby="en_ds_heading"
            >
              <div className="Modal_header">
                <h1 id="en_ds_heading">
                  <img
                    src={errorSign}
                    alt=""
                    aria-hidden="true"
                    className="logoModalutDeleteImg"
                  />{" "}
                  {pendingChange.value === "1" ? "Enable" : "Disable"}
                </h1>
              </div>
              <div className="Modal_middle">
                <div className="inputdrapper">
                  <p id="en_ds_descrip">{getConfirmationText()}</p>
                </div>
              </div>
              <div className="Modal_footer">
                <Box sx={{ display: "flex" }}>
                  <Button
                    variant="outlined"
                    className="cancelBtn"
                    onClick={handleConfirmDialogClose}
                    ref={cancelBtnRef}
                    aria-describedby="en_ds_descrip"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    className="saveBtn"
                    onClick={handleConfirmUpdate}
                  >
                    Yes
                  </Button>
                </Box>
              </div>
            </Box>
          </div>
        </Modal>
      </div>
    </>
  );
};

export default AlertSettings;
